import * as z from 'zod';

export const CreateMenuRequest = z.object({
  menu: z
    .object({
      _id: z
        .string()
        .describe('Menu ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the menu was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the menu was updated.')
        .optional()
        .nullable(),
      name: z.string().describe('Menu name.').min(1).max(500).optional(),
      description: z
        .string()
        .describe('Menu description.')
        .max(1500)
        .optional()
        .nullable(),
      visible: z
        .boolean()
        .describe('Whether the menu visible to site visitors.')
        .optional()
        .nullable(),
      sectionIds: z.array(z.string()).max(100).optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      urlQueryParam: z
        .string()
        .describe(
          'Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.'
        )
        .max(500)
        .optional()
        .nullable(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Custom SEO data for the menu.')
        .optional(),
      businessLocationId: z
        .string()
        .describe(
          'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Menu details.'),
});
export const CreateMenuResponse = z.object({
  _id: z
    .string()
    .describe('Menu ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the menu was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the menu was updated.')
    .optional()
    .nullable(),
  name: z.string().describe('Menu name.').min(1).max(500).optional(),
  description: z
    .string()
    .describe('Menu description.')
    .max(1500)
    .optional()
    .nullable(),
  visible: z
    .boolean()
    .describe('Whether the menu visible to site visitors.')
    .optional()
    .nullable(),
  sectionIds: z.array(z.string()).max(100).optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  urlQueryParam: z
    .string()
    .describe(
      'Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.'
    )
    .max(500)
    .optional()
    .nullable(),
  seoData: z
    .object({
      tags: z
        .array(
          z.object({
            type: z
              .string()
              .describe(
                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
              )
              .optional(),
            props: z
              .record(z.string(), z.any())
              .describe(
                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
              )
              .optional()
              .nullable(),
            meta: z
              .record(z.string(), z.any())
              .describe(
                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
              )
              .optional()
              .nullable(),
            children: z
              .string()
              .describe(
                'SEO tag inner content. For example, `<title> inner content </title>`.'
              )
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
              )
              .optional(),
            disabled: z
              .boolean()
              .describe(
                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
              )
              .optional(),
          })
        )
        .optional(),
      settings: z
        .object({
          preventAutoRedirect: z
            .boolean()
            .describe(
              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
            )
            .optional(),
          keywords: z
            .array(
              z.object({
                term: z.string().describe('Keyword value.').optional(),
                isMain: z
                  .boolean()
                  .describe('Whether the keyword is the main focus keyword.')
                  .optional(),
                origin: z
                  .string()
                  .describe(
                    'The source that added the keyword terms to the SEO settings.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .max(5)
            .optional(),
        })
        .describe('SEO general settings.')
        .optional(),
    })
    .describe('Custom SEO data for the menu.')
    .optional(),
  businessLocationId: z
    .string()
    .describe(
      'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const BulkCreateMenusRequest = z.object({
  menus: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Menu ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the menu was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the menu was updated.')
          .optional()
          .nullable(),
        name: z.string().describe('Menu name.').min(1).max(500).optional(),
        description: z
          .string()
          .describe('Menu description.')
          .max(1500)
          .optional()
          .nullable(),
        visible: z
          .boolean()
          .describe('Whether the menu visible to site visitors.')
          .optional()
          .nullable(),
        sectionIds: z.array(z.string()).max(100).optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        urlQueryParam: z
          .string()
          .describe(
            'Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.'
          )
          .max(500)
          .optional()
          .nullable(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Custom SEO data for the menu.')
          .optional(),
        businessLocationId: z
          .string()
          .describe(
            'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to receive the created menus in the response.')
        .optional(),
    })
    .optional(),
});
export const BulkCreateMenusResponse = z.object({
  results: z
    .array(
      z.object({
        menuMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Whether to receive the created menus in the response.')
          .optional(),
        menu: z
          .object({
            _id: z
              .string()
              .describe('Menu ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the menu was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the menu was updated.')
              .optional()
              .nullable(),
            name: z.string().describe('Menu name.').min(1).max(500).optional(),
            description: z
              .string()
              .describe('Menu description.')
              .max(1500)
              .optional()
              .nullable(),
            visible: z
              .boolean()
              .describe('Whether the menu visible to site visitors.')
              .optional()
              .nullable(),
            sectionIds: z.array(z.string()).max(100).optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            urlQueryParam: z
              .string()
              .describe(
                'Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.'
              )
              .max(500)
              .optional()
              .nullable(),
            seoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('Custom SEO data for the menu.')
              .optional(),
            businessLocationId: z
              .string()
              .describe(
                'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe('Created menu.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
export const GetMenuRequest = z.object({
  menuId: z
    .string()
    .describe('Menu ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetMenuResponse = z.object({
  _id: z
    .string()
    .describe('Menu ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the menu was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the menu was updated.')
    .optional()
    .nullable(),
  name: z.string().describe('Menu name.').min(1).max(500).optional(),
  description: z
    .string()
    .describe('Menu description.')
    .max(1500)
    .optional()
    .nullable(),
  visible: z
    .boolean()
    .describe('Whether the menu visible to site visitors.')
    .optional()
    .nullable(),
  sectionIds: z.array(z.string()).max(100).optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  urlQueryParam: z
    .string()
    .describe(
      'Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.'
    )
    .max(500)
    .optional()
    .nullable(),
  seoData: z
    .object({
      tags: z
        .array(
          z.object({
            type: z
              .string()
              .describe(
                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
              )
              .optional(),
            props: z
              .record(z.string(), z.any())
              .describe(
                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
              )
              .optional()
              .nullable(),
            meta: z
              .record(z.string(), z.any())
              .describe(
                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
              )
              .optional()
              .nullable(),
            children: z
              .string()
              .describe(
                'SEO tag inner content. For example, `<title> inner content </title>`.'
              )
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
              )
              .optional(),
            disabled: z
              .boolean()
              .describe(
                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
              )
              .optional(),
          })
        )
        .optional(),
      settings: z
        .object({
          preventAutoRedirect: z
            .boolean()
            .describe(
              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
            )
            .optional(),
          keywords: z
            .array(
              z.object({
                term: z.string().describe('Keyword value.').optional(),
                isMain: z
                  .boolean()
                  .describe('Whether the keyword is the main focus keyword.')
                  .optional(),
                origin: z
                  .string()
                  .describe(
                    'The source that added the keyword terms to the SEO settings.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .max(5)
            .optional(),
        })
        .describe('SEO general settings.')
        .optional(),
    })
    .describe('Custom SEO data for the menu.')
    .optional(),
  businessLocationId: z
    .string()
    .describe(
      'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const ListMenusRequest = z.object({
  options: z
    .object({
      menuIds: z.array(z.string()).max(500).optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .max(500)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .optional()
            .nullable(),
        })
        .describe('The metadata of the paginated results.')
        .optional(),
      onlyVisible: z
        .boolean()
        .describe(
          'Whether to return only menus that are visible to site visitors.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ListMenusResponse = z.object({
  menus: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Menu ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the menu was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the menu was updated.')
          .optional()
          .nullable(),
        name: z.string().describe('Menu name.').min(1).max(500).optional(),
        description: z
          .string()
          .describe('Menu description.')
          .max(1500)
          .optional()
          .nullable(),
        visible: z
          .boolean()
          .describe('Whether the menu visible to site visitors.')
          .optional()
          .nullable(),
        sectionIds: z.array(z.string()).max(100).optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        urlQueryParam: z
          .string()
          .describe(
            'Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.'
          )
          .max(500)
          .optional()
          .nullable(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Custom SEO data for the menu.')
          .optional(),
        businessLocationId: z
          .string()
          .describe(
            'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('The metadata of the paginated results.')
    .optional(),
});
export const QueryMenusRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          sectionIds: z
            .object({ $hasSome: z.array(z.string()) })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          description: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          urlQueryParam: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          visible: z
            .object({ $eq: z.boolean(), $ne: z.boolean() })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                'name',
                'description',
                'urlQueryParam',
                '_createdDate',
                '_updatedDate',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryMenusResponse = z.object({
  menus: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Menu ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the menu was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the menu was updated.')
          .optional()
          .nullable(),
        name: z.string().describe('Menu name.').min(1).max(500).optional(),
        description: z
          .string()
          .describe('Menu description.')
          .max(1500)
          .optional()
          .nullable(),
        visible: z
          .boolean()
          .describe('Whether the menu visible to site visitors.')
          .optional()
          .nullable(),
        sectionIds: z.array(z.string()).max(100).optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        urlQueryParam: z
          .string()
          .describe(
            'Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.'
          )
          .max(500)
          .optional()
          .nullable(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Custom SEO data for the menu.')
          .optional(),
        businessLocationId: z
          .string()
          .describe(
            'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('The metadata of the paginated results.')
    .optional(),
});
export const UpdateMenuRequest = z.object({
  _id: z
    .string()
    .describe('Menu ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  menu: z
    .object({
      _id: z
        .string()
        .describe('Menu ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the menu was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the menu was updated.')
        .optional()
        .nullable(),
      name: z.string().describe('Menu name.').min(1).max(500).optional(),
      description: z
        .string()
        .describe('Menu description.')
        .max(1500)
        .optional()
        .nullable(),
      visible: z
        .boolean()
        .describe('Whether the menu visible to site visitors.')
        .optional()
        .nullable(),
      sectionIds: z.array(z.string()).max(100).optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      urlQueryParam: z
        .string()
        .describe(
          'Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.'
        )
        .max(500)
        .optional()
        .nullable(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('Custom SEO data for the menu.')
        .optional(),
      businessLocationId: z
        .string()
        .describe(
          'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Menu to update.'),
});
export const UpdateMenuResponse = z.object({
  _id: z
    .string()
    .describe('Menu ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the menu was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the menu was updated.')
    .optional()
    .nullable(),
  name: z.string().describe('Menu name.').min(1).max(500).optional(),
  description: z
    .string()
    .describe('Menu description.')
    .max(1500)
    .optional()
    .nullable(),
  visible: z
    .boolean()
    .describe('Whether the menu visible to site visitors.')
    .optional()
    .nullable(),
  sectionIds: z.array(z.string()).max(100).optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  urlQueryParam: z
    .string()
    .describe(
      'Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.'
    )
    .max(500)
    .optional()
    .nullable(),
  seoData: z
    .object({
      tags: z
        .array(
          z.object({
            type: z
              .string()
              .describe(
                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
              )
              .optional(),
            props: z
              .record(z.string(), z.any())
              .describe(
                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
              )
              .optional()
              .nullable(),
            meta: z
              .record(z.string(), z.any())
              .describe(
                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
              )
              .optional()
              .nullable(),
            children: z
              .string()
              .describe(
                'SEO tag inner content. For example, `<title> inner content </title>`.'
              )
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
              )
              .optional(),
            disabled: z
              .boolean()
              .describe(
                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
              )
              .optional(),
          })
        )
        .optional(),
      settings: z
        .object({
          preventAutoRedirect: z
            .boolean()
            .describe(
              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
            )
            .optional(),
          keywords: z
            .array(
              z.object({
                term: z.string().describe('Keyword value.').optional(),
                isMain: z
                  .boolean()
                  .describe('Whether the keyword is the main focus keyword.')
                  .optional(),
                origin: z
                  .string()
                  .describe(
                    'The source that added the keyword terms to the SEO settings.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .max(5)
            .optional(),
        })
        .describe('SEO general settings.')
        .optional(),
    })
    .describe('Custom SEO data for the menu.')
    .optional(),
  businessLocationId: z
    .string()
    .describe(
      'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const BulkUpdateMenuRequest = z.object({
  menus: z
    .array(
      z.object({
        menu: z
          .object({
            _id: z
              .string()
              .describe('Menu ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.'
              ),
            _createdDate: z
              .date()
              .describe('Date and time the menu was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the menu was updated.')
              .optional()
              .nullable(),
            name: z.string().describe('Menu name.').min(1).max(500).optional(),
            description: z
              .string()
              .describe('Menu description.')
              .max(1500)
              .optional()
              .nullable(),
            visible: z
              .boolean()
              .describe('Whether the menu visible to site visitors.')
              .optional()
              .nullable(),
            sectionIds: z.array(z.string()).max(100).optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            urlQueryParam: z
              .string()
              .describe(
                'Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.'
              )
              .max(500)
              .optional()
              .nullable(),
            seoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('Custom SEO data for the menu.')
              .optional(),
            businessLocationId: z
              .string()
              .describe(
                'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe('Menu to updated.')
          .optional(),
        mask: z.array(z.string()).optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to receive the entity in the response.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateMenuResponse = z.object({
  results: z
    .array(
      z.object({
        menuMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for menu update.')
          .optional(),
        menu: z
          .object({
            _id: z
              .string()
              .describe('Menu ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the menu was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the menu was updated.')
              .optional()
              .nullable(),
            name: z.string().describe('Menu name.').min(1).max(500).optional(),
            description: z
              .string()
              .describe('Menu description.')
              .max(1500)
              .optional()
              .nullable(),
            visible: z
              .boolean()
              .describe('Whether the menu visible to site visitors.')
              .optional()
              .nullable(),
            sectionIds: z.array(z.string()).max(100).optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            urlQueryParam: z
              .string()
              .describe(
                'Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.'
              )
              .max(500)
              .optional()
              .nullable(),
            seoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('Custom SEO data for the menu.')
              .optional(),
            businessLocationId: z
              .string()
              .describe(
                'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Updated menu. Only returned if `returnEntity` is set to `true`.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
export const UpdateExtendedFieldsRequest = z.object({
  _id: z.string().describe('ID of the entity to update.'),
  namespace: z
    .string()
    .describe(
      'Identifier for the app whose extended fields are being updated.'
    ),
  options: z.object({
    namespaceData: z
      .record(z.string(), z.any())
      .describe(
        'Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured.'
      ),
  }),
});
export const UpdateExtendedFieldsResponse = z.object({
  namespace: z.string().describe('Namespace of the app.').optional(),
  namespaceData: z
    .record(z.string(), z.any())
    .describe('Updated extended fields data.')
    .optional()
    .nullable(),
});
export const DeleteMenuRequest = z.object({
  menuId: z
    .string()
    .describe('Menu ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteMenuResponse = z.object({});
export const DuplicateMenuRequest = z.object({
  _id: z
    .string()
    .describe('Menu id to be duplicated.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      businessLocationIds: z.array(z.string()).max(100).optional(),
      duplicateSubEntities: z
        .boolean()
        .describe(
          'Indicates whether sub-entities (such as items) should be duplicated as well.'
        )
        .optional()
        .nullable(),
      menuName: z
        .string()
        .describe('The new name of the duplicated menu.')
        .max(500)
        .optional()
        .nullable(),
    })
    .optional(),
});
export const DuplicateMenuResponse = z.object({
  menuIds: z.array(z.string()).optional(),
});
export const GetMenuSiteUrlRequest = z.object({
  _id: z
    .string()
    .describe('Menu ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetMenuSiteUrlResponse = z.object({
  menuSiteUrl: z
    .object({
      path: z.string().describe('Path URL.').min(1).max(1000).optional(),
    })
    .describe('Retrieved menuInfo with path url.')
    .optional(),
});
export const QueryMenusSiteUrlRequest = z.object({
  options: z
    .object({
      query: z
        .intersection(
          z.object({
            filter: z
              .record(z.string(), z.any())
              .describe(
                'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
              )
              .optional()
              .nullable(),
            sort: z
              .array(
                z.object({
                  fieldName: z
                    .string()
                    .describe('Name of the field to sort by.')
                    .max(512)
                    .optional(),
                  order: z.enum(['ASC', 'DESC']).optional(),
                })
              )
              .optional(),
          }),
          z.xor([
            z.object({ cursorPaging: z.never().optional() }),
            z.object({
              cursorPaging: z
                .object({
                  limit: z
                    .number()
                    .int()
                    .describe('Number of items to load.')
                    .min(0)
                    .max(500)
                    .optional()
                    .nullable(),
                  cursor: z
                    .string()
                    .describe(
                      "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
                ),
            }),
          ])
        )
        .describe('Query options')
        .optional(),
    })
    .optional(),
});
export const QueryMenusSiteUrlResponse = z.object({
  menuSiteUrls: z
    .array(
      z.object({
        path: z.string().describe('Path URL.').min(1).max(1000).optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('The metadata of the paginated results.')
    .optional(),
});
